import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import Div from "@jumbo/shared/Div";
import config from "../../../config/licencePriceCalculator.json";
import { isLicenseVisible } from "./helpers";
// Partner detection is based on localStorage per requirement

const LicenceTable = ({ forceHidePartner = false, priceHeaderLabel }) => {
  const { t, i18n } = useTranslation();

  const { meta = {}, features = {}, licenses = {} } = config || {};

  const getPartnerIdFromLocalStorage = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const pid = Number(user?.customerId);
      return Number.isFinite(pid) && pid > 0 ? pid : null;
    } catch (e) {
      return null;
    }
  };

  // Always detect partner id for visibility filtering.
  // `forceHidePartner` should only hide partner (VOC) pricing display, not license availability.
  const partnerId = getPartnerIdFromLocalStorage();
  const partnerIdForPricing = forceHidePartner ? null : partnerId;

  const licenseOptions = React.useMemo(() => {
    const visibleLicenseEntries = Object.entries(licenses || {}).filter(
      ([key, lic]) => isLicenseVisible(lic, partnerId),
    );

    const entries = visibleLicenseEntries.map(([key, lic]) => ({
      key,
      name: lic?.name || key,
      monthlyPrice: Number(lic?.monthlyPrice || 0),
    }));

    entries.sort((a, b) => {
      if (a.monthlyPrice !== b.monthlyPrice)
        return a.monthlyPrice - b.monthlyPrice;
      return String(a.name).localeCompare(String(b.name));
    });

    return entries;
  }, [licenses, partnerId]);

  const resolveLangKey = (lang) => {
    if (!lang) return "en";
    // Map app language codes to JSON label keys where needed
    if (lang === "cs") return "cz";
    return lang;
  };

  const getLocalizedLabel = (labels) => {
    const lang = resolveLangKey(i18n?.language);
    if (labels && typeof labels === "object") {
      if (labels[lang]) return labels[lang];
      const fallbacks = ["en", "sk", "cs", "de"].map(resolveLangKey);
      for (const fb of fallbacks) {
        if (labels[fb]) return labels[fb];
      }
      const first = Object.values(labels).find((v) => typeof v === "string");
      if (first) return first;
    }
    return "";
  };

  const getInProgressLabel = () => {
    const lang = resolveLangKey(i18n?.language);
    const map = {
      sk: "Pripravujeme",
      cz: "Připravujeme",
      en: "In progress",
      de: "In Vorbereitung",
    };
    return map[lang] || map.en;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined) return "—";
    const currency =
      (meta?.currency || "EUR") === "EUR" ? "€" : meta?.currency || "€";
    return `${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ${currency}`;
  };

  const getPartnerPriceForLicense = (license, partnerId) => {
    if (!license || !partnerId) return null;
    const partners = license?.partners || {};
    const entry = Object.values(partners || {}).find(
      (p) => Number(p?.id) === Number(partnerId),
    );
    return entry && typeof entry.price !== "undefined"
      ? Number(entry.price)
      : null;
  };

  const isPartnerRecognized =
    !forceHidePartner &&
    partnerIdForPricing &&
    licenseOptions.some(
      (opt) =>
        getPartnerPriceForLicense(
          (config?.licenses || {})[opt.key],
          partnerIdForPricing,
        ) !== null,
    );

  const renderBool = (val) =>
    val ? (
      <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
    ) : (
      <HighlightOffOutlinedIcon sx={{ color: "error.main" }} />
    );

  const featureKeys = Object.keys(features || {}); // preserves JSON order

  // Handle empty state when no licenses are visible
  if (licenseOptions.length === 0) {
    return (
      <Div>
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            {t("pages.priceCalculator.noLicensesAvailable", {
              defaultValue: "No license types are currently available.",
            })}
          </Typography>
        </Paper>
      </Div>
    );
  }

  return (
    <Div>
      <Paper variant="outlined">
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 700 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t("pages.dsokDetails.functionality", {
                    defaultValue: "Functionality",
                  })}
                </TableCell>
                {licenseOptions.map((opt) => {
                  const lic = licenses[opt.key] || {};
                  return (
                    <TableCell
                      key={opt.key}
                      align="center"
                      sx={{ fontWeight: 600 }}
                    >
                      {lic.name || opt.key}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {featureKeys.map((featureKey) => {
                const feature = features[featureKey] || {};
                const label = getLocalizedLabel(feature.labels);
                return (
                  <TableRow key={featureKey}>
                    <TableCell component="th" scope="row">
                      <Box component="span">
                        {label || featureKey}
                        {feature?.status === "inProgress" ? (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1, fontStyle: "italic" }}
                          >
                            ({getInProgressLabel()})
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    {licenseOptions.map((opt) => {
                      const lic = licenses[opt.key] || {};
                      const available = Boolean(lic?.features?.[featureKey]);
                      return (
                        <TableCell
                          key={`${featureKey}-${opt.key}`}
                          align="center"
                        >
                          {renderBool(available)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  {priceHeaderLabel ||
                    t("pages.priceCalculator.moc", { defaultValue: "MOC" })}
                </TableCell>
                {licenseOptions.map((opt) => {
                  const lic = licenses[opt.key] || {};
                  return (
                    <TableCell
                      key={`price-${opt.key}`}
                      align="center"
                      sx={{ fontWeight: 600 }}
                    >
                      {formatPrice(lic?.monthlyPrice)}
                    </TableCell>
                  );
                })}
              </TableRow>

              {isPartnerRecognized && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 600 }}
                  >
                    VOC
                  </TableCell>
                  {licenseOptions.map((opt) => {
                    const lic = licenses[opt.key] || {};
                    const partnerPrice = getPartnerPriceForLicense(
                      lic,
                      partnerId,
                    );
                    return (
                      <TableCell
                        key={`partner-${opt.key}`}
                        align="center"
                        sx={{ fontWeight: 600 }}
                      >
                        {partnerPrice !== null
                          ? formatPrice(partnerPrice)
                          : "—"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Div>
  );
};

export default LicenceTable;
